import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { formatDate } from '@/utils/dateFormat';
import { StatusBadge } from '@/components/ui/status-badge';
import { 
  Calendar, Package, Clock, CheckCircle2, 
  Loader2, AlertCircle, MapPin, Phone, RefreshCw 
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STERISUL_LOGO = "/sterisul-logo.png";

export default function RastreamentoClientePage() {
  const { clienteId } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (clienteId) {
      fetchData();
    }
  }, [clienteId]);

  const fetchData = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API}/rastreamento/cliente/${clienteId}`);
      setData(response.data);
      setError(null);
    } catch (error) {
      setError('Não foi possível carregar os dados. Verifique o link.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'aguardando_retirada':
        return <Package className="w-5 h-5 text-orange-500" />;
      case 'retirado':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'em_processamento':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-slate-400" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'aguardando_retirada':
        return 'Pronto para Retirada';
      case 'retirado':
        return 'Retirado';
      case 'em_processamento':
        return 'Em Processamento';
      case 'pendente':
        return 'Aguardando Esterilização';
      case 'aprovado':
        return 'Esterilizado';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Carregando seus materiais...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-red-50 to-white px-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Ops!</h1>
          <p className="text-slate-600 mb-4">{error}</p>
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Tentar Novamente
          </Button>
        </div>
      </div>
    );
  }

  const { cliente, itens_arsenal, ciclos_ativos } = data;

  return (
    <div data-testid="rastreamento-page" className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <div className="bg-blue-600 text-white px-4 py-6">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <img 
              src={STERISUL_LOGO} 
              alt="Sterisul" 
              className="w-12 h-12 object-contain bg-white rounded-lg p-1"
            />
            <div>
              <h1 className="text-lg font-bold">Sterisul</h1>
              <p className="text-xs text-blue-100">Rastreamento de Materiais</p>
            </div>
          </div>
        </div>
      </div>

      {/* Client Info */}
      <div className="max-w-lg mx-auto px-4 -mt-4">
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Cliente</p>
              <h2 className="text-lg font-bold text-slate-900">{cliente?.nome}</h2>
              {cliente?.telefone && (
                <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                  <Phone className="w-3 h-3" />
                  {cliente.telefone}
                </p>
              )}
            </div>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={fetchData}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Ready for Pickup Section */}
        {itens_arsenal && itens_arsenal.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Package className="w-4 h-4 text-orange-500" />
              Prontos para Retirada ({itens_arsenal.length})
            </h3>
            <div className="space-y-3">
              {itens_arsenal.map((item) => (
                <div 
                  key={item.id} 
                  className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(item.status)}
                      <span className="text-sm font-semibold text-orange-700">
                        {getStatusLabel(item.status)}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500">
                      {formatDate(item.data_entrada)}
                    </span>
                  </div>
                  <div className="mt-2">
                    <p className="text-xs text-slate-500 mb-1">Materiais:</p>
                    <div className="flex flex-wrap gap-1">
                      {item.materiais?.map((mat, idx) => (
                        <span 
                          key={idx}
                          className="text-xs bg-white px-2 py-1 rounded-full border border-orange-200"
                        >
                          {mat.nome}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Cycles Section */}
        {ciclos_ativos && ciclos_ativos.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-500" />
              Em Processamento ({ciclos_ativos.length})
            </h3>
            <div className="space-y-3">
              {ciclos_ativos.map((ciclo) => (
                <div 
                  key={ciclo.id} 
                  className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-900">
                      {ciclo.numero_ciclo}
                    </span>
                    <StatusBadge status={ciclo.resultado} />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(ciclo.data_hora)}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Val: {formatDate(ciclo.data_validade)}
                    </div>
                  </div>
                  {ciclo.materiais && ciclo.materiais.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-100">
                      <div className="flex flex-wrap gap-1">
                        {ciclo.materiais.map((mat, idx) => (
                          <span 
                            key={idx}
                            className="text-xs bg-slate-100 px-2 py-1 rounded-full"
                          >
                            {mat.nome}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {(!itens_arsenal || itens_arsenal.length === 0) && 
         (!ciclos_ativos || ciclos_ativos.length === 0) && (
          <div className="text-center py-12">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Tudo em dia!</h3>
            <p className="text-slate-600 text-sm">
              Você não tem materiais pendentes ou em processamento.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-8 text-xs text-slate-400">
          <p>Sterisul - Centro de Esterilização</p>
          <p className="mt-1">Conformidade ANVISA/RDC 15</p>
        </div>
      </div>

      {/* Install PWA Banner */}
      <div id="pwa-install-banner" className="hidden fixed bottom-0 left-0 right-0 bg-blue-600 text-white p-4 shadow-lg">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={STERISUL_LOGO} alt="Sterisul" className="w-8 h-8 bg-white rounded p-0.5" />
            <div>
              <p className="font-semibold text-sm">Instalar Sterisul</p>
              <p className="text-xs text-blue-100">Acesse mais rápido</p>
            </div>
          </div>
          <Button 
            size="sm" 
            variant="secondary"
            onClick={() => document.getElementById('pwa-install-banner').classList.add('hidden')}
          >
            Instalar
          </Button>
        </div>
      </div>
    </div>
  );
}
